import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  BusinessConflict,
  ConflictChoice,
  ConflictGuardian,
  ConflictSource,
  Resolutions,
  isParentUnlink,
  isTerminal,
} from './conflict.model';

/** What went wrong with the last unlink save, shown above the cards. */
export type UnlinkNotice = 'network' | 'stale' | 'permission' | null;

/**
 * The whole answer to one business conflict, for any form that can hit one.
 *
 * Two full records never fit beside the fields that caused them, so the
 * comparison lives in a dialog of its own: the form keeps a one-line strip
 * under the anchoring control and the dialog carries the cards. Drop the tag
 * where the conflict belongs and wire the same three bindings the endpoint
 * already speaks — the student modal and the parent modal share it as is.
 *
 *   <app-conflict-resolver *ngIf="showsConflictAt('parent_phone')"
 *       [conflict]="conflict" [resolutions]="resolutions" [lang]="lang"
 *       (choose)="onConflictChoice($event)" (undo)="onConflictUndo($event)">
 *
 * S1 with `remove` on offer is the one case saved from inside the dialog: the
 * admin unlinks a parent and presses Unlink & save there, so the dialog stays
 * open through the request and reports what came back (`saving`, `notice`).
 */
@Component({
  selector: 'app-conflict-resolver',
  templateUrl: './conflict-resolver.component.html',
  styleUrls: ['./conflict-resolver.component.scss'],
})
export class ConflictResolverComponent implements OnChanges {
  @Input() conflict: BusinessConflict;
  @Input() resolutions: Resolutions = {};
  @Input() lang = 'en';

  /** S1 unlink only: a save is running. */
  @Input() saving = false;
  /** S1 unlink only: the outcome of the last save that did not go through. */
  @Input() notice: UnlinkNotice = null;
  /** S1 unlink only: parents new to the list since the last answer. */
  @Input() updatedIds: number[] = [];

  @Output() choose = new EventEmitter<ConflictChoice>();
  @Output() undo = new EventEmitter<string>();
  /** S1 unlink only: Unlink & save. */
  @Output() save = new EventEmitter<void>();

  /** Open on arrival: an unanswered conflict is the one thing blocking the save. */
  open = false;

  ngOnChanges(changes: SimpleChanges): void {
    // A replaced conflict is a new question — including the next one on the
    // same field — so it asks itself again.
    if (changes['conflict']) {
      this.open = !!this.conflict;
    }
  }

  get terminal(): boolean {
    return isTerminal(this.conflict);
  }

  get unlink(): boolean {
    return isParentUnlink(this.conflict);
  }

  /** The parent picked for unlinking, for the footer line. */
  get unlinkTarget(): ConflictGuardian | null {
    const id = this.resolutions?.student_parent_limit?.user_id;
    return this.conflict?.parents?.find(p => p.user_id === id) || null;
  }

  get canSave(): boolean {
    return !!this.unlinkTarget && !this.saving && this.notice !== 'permission';
  }

  get titleKey(): string {
    if (this.unlink) {
      return 'student-conflicts.limit-title';
    }
    return this.terminal ? 'student-conflicts.modal-title-blocked' : 'student-conflicts.modal-title';
  }

  get hintKey(): string {
    if (this.unlink) {
      return 'student-conflicts.limit-hint';
    }
    return this.terminal ? 'student-conflicts.modal-hint-blocked' : 'student-conflicts.modal-hint';
  }

  get chosen(): ConflictSource | null {
    return (this.resolutions?.[this.conflict?.field]?.source as ConflictSource) || null;
  }

  /**
   * Re-opening is only worth offering when there was a genuine choice. With a
   * single allowed source the dialog would just show the one button they
   * already pressed; editing the field still clears the answer either way.
   */
  get canReopen(): boolean {
    return this.terminal || !this.chosen || this.conflict?.allowed_sources?.length > 1;
  }

  get actionKey(): string {
    if (this.terminal) {
      return 'student-conflicts.details';
    }
    return this.chosen ? 'student-conflicts.change' : 'student-conflicts.review';
  }

  /**
   * Answering is the whole point of the dialog: once answered it steps aside —
   * except for an unlink, which is saved from here.
   */
  onChoose(choice: ConflictChoice): void {
    this.choose.emit(choice);
    if (!this.unlink) {
      this.open = false;
    }
  }

  onSave(): void {
    if (this.canSave) {
      this.save.emit();
    }
  }

  /** Coming back to an answered conflict drops the answer, so the dialog asks again. */
  reopen(): void {
    if (this.chosen && !this.terminal) {
      this.undo.emit(this.conflict.field);
    }
    this.open = true;
  }

  /**
   * Leaving an unlink unsaved keeps both parents: the choice is dropped, so
   * the form's own Save cannot send it later without the admin seeing it.
   */
  close(): void {
    if (this.unlink) {
      if (this.saving) {
        return;
      }
      if (this.chosen) {
        this.undo.emit(this.conflict.field);
      }
    }
    this.open = false;
  }

  /** The unlink dialog has its own confirm on top; Escape closes that one, not this. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.unlink) {
      this.close();
    }
  }
}
